<script setup lang="ts">
import axios from 'axios'
import { Star } from 'lucide-vue-next'

import ReviewTemplatesForm from './ReviewTemplatesForm.vue'
import useReview from '@/composables/review'
import type { reviewReplyTemplateInterface } from '@/types/review'

const MAX_FAVORITES = 5

const props = defineProps<{
  template: reviewReplyTemplateInterface
  favoritesNumber: number
}>()

const emit = defineEmits<{
  (e: 'updated', template: reviewReplyTemplateInterface): void
  (e: 'deleted'): void
  (e: 'toggleFavorite', isFavorite: boolean): void
}>()

const { t } = useI18n()
const notification = useNotification()
const { resolveMoodLabel, resolveMoodVariant } = useReview()

const displayForm = ref(false)
const isProcessing = ref(false)

const isFavoriteDisabled = computed(() => !props.template.isFavorite && props.favoritesNumber >= MAX_FAVORITES)

const favoriteTooltip = computed(() => {
  if (props.template.isFavorite) {
    return t('pages.review.reviewPage.templates.tooltip.removeFromFavorites')
  }

  return isFavoriteDisabled.value
    ? t('pages.review.reviewPage.templates.tooltip.cantAddToFavorites', { number: MAX_FAVORITES })
    : t('pages.review.reviewPage.templates.tooltip.addToFavorites')
})

const templateUpdated = (template: reviewReplyTemplateInterface) => {
  emit('updated', template)
  displayForm.value = false
}

const templateDeleted = async () => {
  isProcessing.value = true
  try {
    await axios.delete(`/reviews-reply-templates/${props.template.id}`)
    notification.create('success', {
      text: t('pages.review.reviewPage.notifications.success.deleteTemplate')
    })
    displayForm.value = false
    emit('deleted')
  } catch {
    notification.create('error', {
      text: t('pages.review.reviewPage.notifications.error.deleteTemplate'),
      persist: true
    })
    isProcessing.value = false
  }
}

const toggleFavorite = async () => {
  if (isFavoriteDisabled.value) return

  const isFavorite = !props.template.isFavorite

  try {
    await axios.put(`/reviews-reply-templates/${props.template.id}`, {
      title: props.template.title,
      content: props.template.content,
      emoji: props.template.emoji,
      reviewReplyTemplateMoodId: props.template.reviewReplyTemplateMood?.id,
      isFavorite
    })
    emit('toggleFavorite', isFavorite)
    notification.create('success', {
      text: isFavorite
        ? t('pages.review.reviewPage.notifications.success.addTemplateToFavorite')
        : t('pages.review.reviewPage.notifications.success.removeTemplateFromFavorite')
    })
  } catch {
    notification.create('error', {
      text: isFavorite
        ? t('pages.review.reviewPage.notifications.error.addTemplateToFavorite')
        : t('pages.review.reviewPage.notifications.error.removeTemplateFromFavorite'),
      persist: true
    })
  }
}
</script>

<template>
  <ReviewTemplatesForm
    v-if="displayForm"
    :template="template"
    @canceled="displayForm = false"
    @updated="templateUpdated"
  />

  <Card v-else class="gap-0 py-4">
    <CardContent class="flex flex-col gap-4 px-4 lg:flex-row lg:items-start">
      <div class="shrink-0 text-center text-4xl lg:w-12">
        {{ template.emoji }}
      </div>

      <div class="flex flex-1 flex-col gap-2">
        <div class="flex flex-wrap items-center gap-1">
          <BaseChip
            :color="resolveMoodVariant(template.reviewReplyTemplateMood?.id)"
            :text="t(resolveMoodLabel(template.reviewReplyTemplateMood?.id) ?? '')"
          />
          <AppTooltip tooltip-position="top">
            <Button
              variant="ghost"
              size="icon-sm"
              :aria-disabled="isFavoriteDisabled"
              :aria-label="favoriteTooltip"
              @click="toggleFavorite"
            >
              <Star
                :class="[
                  template.isFavorite && 'fill-primary text-primary',
                  !template.isFavorite && (isFavoriteDisabled ? 'text-muted-foreground' : 'text-primary')
                ]"
              />
            </Button>
            <template #tooltip-content>
              {{ favoriteTooltip }}
            </template>
          </AppTooltip>
        </div>

        <h3 class="text-base font-semibold text-foreground">
          {{ template.title }}
        </h3>

        <p class="text-sm text-muted-foreground">
          {{ template.content }}
        </p>
      </div>

      <div class="flex shrink-0 gap-2">
        <Button variant="default-primary" @click="displayForm = true">
          {{ t('pages.review.reviewSettingsPage.templates.buttons.modify') }}
        </Button>
        <BaseConfirmButton
          :processing="isProcessing"
          :title="t('pages.review.reviewPage.templates.confirm.title')"
          :text="t('pages.review.reviewPage.templates.confirm.text')"
          :cancel-button-label="t('pages.review.reviewPage.templates.confirm.cancel')"
          :confirm-button-label="t('pages.review.reviewPage.templates.confirm.confirm')"
          @confirm="templateDeleted"
        >
          {{ t('pages.review.reviewSettingsPage.templates.buttons.delete') }}
        </BaseConfirmButton>
      </div>
    </CardContent>
  </Card>
</template>
